import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import type { I18nText } from "@/api/types";

export const tx = (t: I18nText | undefined, lang: string) => (t ? t[lang] ?? t.el ?? t.en ?? "" : "");

// ---------- buttons ----------
type Variant = "primary" | "secondary" | "ghost" | "quiet" | "danger" | "danger-quiet";
const VARIANT: Record<Variant, string> = {
  primary: "bg-accent text-white hover:bg-accent-strong",
  secondary: "border-line-strong bg-surface text-ink hover:bg-surface-2",
  ghost: "border-line-strong bg-surface text-ink hover:bg-surface-2",
  quiet: "bg-transparent text-muted hover:bg-surface-2 hover:text-ink",
  danger: "bg-danger text-white hover:brightness-95",
  "danger-quiet": "bg-danger-soft text-danger hover:brightness-95",
};
/** `ghost` is kept as an alias of `secondary` (bordered) because most pages use it for the second action. */
export function Button({ variant = "primary", size = "md", loading = false, className = "", children, ...p }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "md" | "sm"; loading?: boolean }) {
  const base = "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-ctl border border-transparent font-medium disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent";
  const sz = size === "sm" ? "h-[30px] px-2.5 text-[13px]" : "h-9 px-3.5 text-sm";
  return <button {...p} disabled={p.disabled || loading} className={`${base} ${sz} ${VARIANT[variant]} ${className}`}>{loading && <span className="ui-spin" />}{children}</button>;
}

// ---------- fields ----------
export function Field({ label, children, hint, error, className = "" }: { label: string; children: ReactNode; hint?: string; error?: string | null; className?: string }) {
  return (
    <label className={`block min-w-0 text-[13px] ${className}`}>
      <span className="mb-1 block font-medium text-ink">{label}</span>
      {children}
      {error ? <span className="mt-1 block text-xs text-danger">{error}</span> : hint ? <span className="mt-1 block text-xs text-muted">{hint}</span> : null}
    </label>
  );
}
const control = "w-full rounded-ctl border border-line-strong bg-surface px-2.5 text-sm text-ink placeholder:text-faint focus-visible:border-accent focus-visible:outline-none disabled:bg-surface-2 disabled:text-muted";
export function Input({ invalid, className = "", ...p }: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return <input {...p} className={`${control} h-9 ${invalid ? "border-danger shadow-[0_0_0_3px_rgb(var(--c-danger-soft))]" : ""} ${className}`} />;
}
export function Textarea({ className = "", ...p }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...p} className={`${control} min-h-[76px] py-2 ${className}`} />;
}
export function Select({ className = "", ...p }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...p} className={`ui-select ${control} h-9 ${className}`} />;
}
export function Toggle({ checked, onChange, label, disabled, title }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean; title?: string }) {
  return (
    <label className={`inline-flex items-center gap-2.5 text-sm ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`} title={title}>
      <input type="checkbox" className="peer sr-only" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className={`relative h-5 w-9 rounded-full transition-colors peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent ${checked ? "bg-accent" : "bg-line-strong"}`}><span className={`absolute top-0.5 h-4 w-4 rounded-full bg-surface shadow transition-[left] ${checked ? "left-[18px]" : "left-0.5"}`} /></span>
      {label}
    </label>
  );
}
export function Segmented<T extends string>({ value, onChange, options, className = "" }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode; title?: string }[]; className?: string }) {
  return (
    <div className={`inline-flex gap-0.5 rounded-ctl bg-surface-2 p-[3px] ${className}`} role="group">
      {options.map((o) => <button key={o.value} type="button" title={o.title} className={`h-7 rounded px-3 text-[13px] font-medium ${o.value === value ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink"}`} onClick={() => onChange(o.value)}>{o.label}</button>)}
    </div>
  );
}
export function Chip({ active, onClick, onRemove, children, className = "", title }: { active?: boolean; onClick?: () => void; onRemove?: () => void; children: ReactNode; className?: string; title?: string }) {
  const Tag = onClick ? "button" : "span";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} title={title} className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[13px] ${active ? "border-transparent bg-accent-soft text-accent-ink" : "border-line-strong bg-surface text-ink"} ${onClick ? "hover:bg-surface-2" : ""} ${className}`}>
      {children}{onRemove && <button type="button" className="-mr-1 px-0.5 text-muted hover:text-ink" aria-label="remove" onClick={(e) => { e.stopPropagation(); onRemove(); }}>×</button>}
    </Tag>
  );
}
export function Tabs<T extends string>({ value, onChange, tabs, className = "" }: { value: T; onChange: (v: T) => void; tabs: { value: T; label: ReactNode; disabled?: boolean; title?: string }[]; className?: string }) {
  return (
    <div className={`flex gap-0.5 overflow-x-auto border-b ${className}`} role="tablist">
      {tabs.map((tb) => <button key={tb.value} type="button" role="tab" aria-selected={tb.value === value} disabled={tb.disabled} title={tb.title} className={`-mb-px h-9 whitespace-nowrap border-b-2 px-3 text-sm disabled:cursor-not-allowed disabled:opacity-50 ${tb.value === value ? "border-accent font-medium text-ink" : "border-transparent text-muted hover:text-ink"}`} onClick={() => onChange(tb.value)}>{tb.label}</button>)}
    </div>
  );
}

// ---------- badges ----------
export function Badge({ children, tone = "gray", className = "", title }: { children: ReactNode; tone?: "gray" | "green" | "red" | "amber" | "blue" | "accent" | "solid"; className?: string; title?: string }) {
  const c = { gray: "bg-surface-2 text-muted", green: "bg-ok-soft text-ok", red: "bg-danger-soft text-danger", amber: "bg-warn-soft text-warn", blue: "bg-info-soft text-info", accent: "bg-accent-soft text-accent-ink", solid: "bg-accent text-white" }[tone];
  return <span title={title} className={`inline-flex h-[22px] items-center gap-1 whitespace-nowrap rounded-md px-2 text-xs font-medium ${c} ${className}`}>{children}</span>;
}

// ---------- surfaces ----------
/** Centred modal (kept under its historical name so every call site stays the same). Escape and a click on the
 * backdrop close it; the body stays put while it is open. `footer` renders an action bar on surface-2. */
export function Drawer({ open, onClose, title, children, wide, footer }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; wide?: boolean; footer?: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-ink/45 p-2 sm:p-6" role="dialog" aria-modal="true" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`my-auto flex w-full max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-3rem)] flex-col ${wide ? "max-w-5xl" : "max-w-3xl"} overflow-hidden rounded-modal bg-surface shadow-modal`}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h2 className="text-base font-semibold">{title}</h2>
          <button type="button" className="rounded px-2 text-muted hover:bg-surface-2 hover:text-ink" onClick={onClose} aria-label="close">✕</button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">{children}</div>
        {footer && <div className="flex flex-wrap items-center justify-end gap-2 border-t bg-surface-2 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}
/** Settings block: title + explanation on the left, fields on the right (full width, stacks on phones). */
export function SettingsSection({ title, hint, children, actions }: { title: string; hint?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="grid gap-4 rounded-card border bg-surface p-5 lg:grid-cols-[240px_1fr]">
      <div><h2 className="text-[15px] font-semibold">{title}</h2>{hint && <p className="mt-1 max-w-[32ch] text-xs text-muted">{hint}</p>}{actions && <div className="mt-3">{actions}</div>}</div>
      <div className="min-w-0 space-y-3">{children}</div>
    </section>
  );
}
/** Every page starts the same way: eyebrow (menu group), title, one line of context, the actions on the right. */
export function PageHeader({ eyebrow, title, meta, actions, className = "" }: { eyebrow?: ReactNode; title: ReactNode; meta?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={`flex flex-wrap items-end justify-between gap-x-4 gap-y-2 ${className}`}>
      <div className="min-w-0">
        {eyebrow && <div className="text-[11px] font-semibold uppercase tracking-[.08em] text-muted">{eyebrow}</div>}
        <h1 className="text-[22px] font-semibold leading-tight">{title}</h1>
        {meta && <div className="mt-0.5 text-[13px] text-muted">{meta}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
export function EmptyState({ title, hint, action, className = "" }: { title: ReactNode; hint?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={`grid justify-items-center gap-2 rounded-card border border-dashed border-line-strong px-4 py-9 text-center text-sm text-muted ${className}`}>
      <b className="font-semibold text-ink">{title}</b>{hint && <span>{hint}</span>}{action}
    </div>
  );
}
export function Skeleton({ lines = 3, className = "" }: { lines?: number; className?: string }) {
  const widths = ["40%", "100%", "85%", "60%", "90%"];
  return <div className={`grid gap-2.5 ${className}`} aria-hidden>{Array.from({ length: lines }, (_, i) => <div key={i} className="ui-skeleton h-3" style={{ width: widths[i % widths.length] }} />)}</div>;
}

// ---------- toasts ----------
type Toast = { id: number; message: ReactNode; tone: "ok" | "error" | "info"; action?: { label: string; onClick: () => void } };
type ToastApi = { show: (message: ReactNode, opts?: { tone?: Toast["tone"]; action?: Toast["action"] }) => void; error: (message: ReactNode) => void };
const ToastCtx = createContext<ToastApi | null>(null);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback<ToastApi["show"]>((message, opts) => {
    const id = ++seq.current;
    const tone = opts?.tone ?? "ok";
    setItems((xs) => [...xs.slice(-3), { id, message, tone, action: opts?.action }]);
    if (tone !== "error") window.setTimeout(() => dismiss(id), 4000);
  }, [dismiss]);
  const api = useMemo<ToastApi>(() => ({ show, error: (m) => show(m, { tone: "error" }) }), [show]);
  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[min(380px,calc(100vw-2rem))] flex-col gap-2" aria-live="polite">
        {items.map((tst) => (
          <div key={tst.id} className={`pointer-events-auto flex items-start gap-3 rounded-card px-3.5 py-2.5 text-[13px] text-white shadow-pop ${tst.tone === "error" ? "bg-danger" : tst.tone === "info" ? "bg-info" : "bg-accent-ink"}`} role={tst.tone === "error" ? "alert" : "status"}>
            <span className="min-w-0 flex-1">{tst.message}</span>
            {tst.action && <button type="button" className="underline opacity-90 hover:opacity-100" onClick={() => { tst.action!.onClick(); dismiss(tst.id); }}>{tst.action.label}</button>}
            <button type="button" className="opacity-70 hover:opacity-100" aria-label="close" onClick={() => dismiss(tst.id)}>✕</button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export function useToast(): ToastApi {
  const v = useContext(ToastCtx);
  if (!v) return { show: () => {}, error: () => {} };
  return v;
}
